import { ReactElement, useMemo } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { Button, FlosIcon } from '@flos/react-ui';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { currencyFormatter } from 'common/helpers/CommonUtil';
import { formatDateIntlCustom } from 'common/helpers/date';
import { getProductValue } from 'common/helpers/product';

import { TGiftCard } from '../Home.page';

import styles from './GiftCardTable.module.css';

/* ==================================== Local Types ==================================== */
type TUseGetTableColumns = (
  hasAction: Parameters<TGiftCardTable>[0]['hasAction'],
  navigate: NavigateFunction,
  onOpenDeleteGiftCardModal?: Parameters<TGiftCardTable>[0]['onOpenDeleteGiftCardModal']
) => GridColDef[];

type TEmptyTable = () => ReactElement;

type TGiftCardTable = (props: {
  isLoading: boolean;
  giftCards: Array<TGiftCard>;
  hasAction: boolean;
  onOpenDeleteGiftCardModal?: (props: { id: string; key: string }) => void;
}) => ReactElement;

/* ==================================== Local Functions ==================================== */
const useGetTableColumns: TUseGetTableColumns = (hasAction, navigate, onOpenDeleteGiftCardModal) => {
  const tableColumns: GridColDef[] = useMemo(() => {
    const tableColumns: GridColDef[] = [
      { field: 'productLabel', headerName: 'Product', width: 130 },
      {
        field: 'amount',
        headerName: 'Amount (DKK)',
        width: 175,
        valueGetter: (amount) => currencyFormatter(amount)
      },
      {
        field: 'startDate',
        headerName: 'Start Date',
        width: 270,
        valueGetter: (startDate) => formatDateIntlCustom(startDate)
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        width: 270,
        valueGetter: (endDate) => formatDateIntlCustom(endDate)
      }
    ];

    if (hasAction) {
      tableColumns.push({
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        display: 'flex',
        width: 250,
        renderCell: ({ row }) => (
          <div className={styles['table-cell-actions']}>
            <Button theme="ghost" onClick={() => navigate('/edit', { state: { ...row } })}>
              Opdatering
            </Button>
            <Button
              theme="ghost"
              iconShape="trash-can"
              onClick={() => onOpenDeleteGiftCardModal!({ key: getProductValue(row.product), id: row.id })}
            />
          </div>
        )
      });
    }

    return tableColumns;
  }, [hasAction, navigate, onOpenDeleteGiftCardModal]);

  return tableColumns;
};

/* ==================================== Local Components ==================================== */
const EmptyTable: TEmptyTable = () => {
  return (
    <div className={styles['empty-table']}>
      <FlosIcon shape="creditcard-clock" size="xlarge" />
      Der er ingen gavekort
    </div>
  );
};

/* ==================================== Main Component ==================================== */
const GiftCardTable: TGiftCardTable = ({ isLoading, giftCards, hasAction, onOpenDeleteGiftCardModal }) => {
  const navigate = useNavigate();

  const tableColumns = useGetTableColumns(hasAction, navigate, onOpenDeleteGiftCardModal);

  return (
    <>
      <div className={styles['root']}>
        {!isLoading && giftCards.length === 0 ? (
          <EmptyTable />
        ) : (
          <DataGrid
            className={styles['table']}
            loading={isLoading}
            rows={giftCards}
            columns={tableColumns}
            autosizeOptions={{
              includeHeaders: true,
              includeOutliers: false,
              expand: true
            }}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10]}
            disableRowSelectionOnClick
          />
        )}
      </div>
    </>
  );
};

export default GiftCardTable;
